import type { ReactNode } from 'react';
import { AppLayout } from '../templates/AppLayout/AppLayout';
import { Header } from '../layout/Header';
import { Sidebar } from '../Sidebar';
import { PageContent } from '../templates/PageContent';
import { PageHeader } from '../templates/PageHeader';
import { Card } from '../atoms/Card';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Text } from '../atoms/Typography';
import { useTranslation } from '../../i18n/useTranslation';
import { useZeovitAvailability } from '../../hooks/useZeovitAvailability';
import { ZeovitUnavailableNotice } from './ZeovitUnavailableNotice';

export interface ZeovitModuleGateProps {
  aquariumId: string | undefined;
  /** The page's title, kept on the loading and info states. */
  title: string;
  width?: 'narrow' | 'default' | 'wide';
  /** The ZeoVit page itself; only mounted (and fetching) for a ZeoVit aquarium. */
  children: ReactNode;
}

/**
 * Wraps a ZeoVit page (#1784): for an aquarium whose supply system is not ZeoVit it
 * renders the page's title and the info notice (no module, no actions, no ZeoVit
 * requests) instead of the page. While the aquarium list loads it shows a loading
 * state; when the aquarium cannot be resolved the page renders as before and the
 * backend gate decides.
 */
export function ZeovitModuleGate({ aquariumId, title, width = 'default', children }: ZeovitModuleGateProps) {
  const { t } = useTranslation();
  const availability = useZeovitAvailability(aquariumId);

  if (availability === 'available' || availability === 'unknown' || !aquariumId) {
    return <>{children}</>;
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width={width}>
          <PageHeader title={title} />
          {availability === 'loading' ? (
            <Card>
              <LiveRegion busy>
                <Text color="muted">{t('zeovit.unavailable.checking')}</Text>
              </LiveRegion>
            </Card>
          ) : (
            <ZeovitUnavailableNotice aquariumId={aquariumId} />
          )}
        </PageContent>
      }
    />
  );
}

export default ZeovitModuleGate;
